import * as React from 'react';
import { useState } from 'react';

import { computeText } from '@/locales/utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { Text } from '@/components/atoms/Text';
import { SvyButton } from '@/components/savory/SvyButton';
import { confirm } from '@/core/asyncActions/confirm';
import { FormControl, FormControls, SuccessMessage, LogsContainer, LogLine } from '../AdminTools.style';
import { ENV } from '@/utils/env';
import { Form } from '@/core/form/Form';
import { FileInput } from '@/components/form/FileInput';
import { Accept } from 'react-dropzone';
import {
    DescriptionList,
    DropzoneContainer,
    TextContainer,
} from '@/pages/MenuList/components/MenuModal/CreateMenuModal/ImportMenuStep/ImportMenuStep.style';
import { useSelector } from 'react-redux';
import { getUserToken } from '@/redux/user';

export type EditProductInfoFromProductIdInputs = {
    file: File;
};

const ALLOWED_FILE_TYPES: Accept = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
};

const EditProductInfoFromProductId: React.FunctionComponent = () => {
    const intl = useIntl();
    const token = useSelector(getUserToken);
    const [logs, setLogs] = useState<string[]>([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const handleSubmitForm = async ({ file }: EditProductInfoFromProductIdInputs) => {
        const hasConfirmed = await confirm(computeText(intl, 'adminTools.confirmModal.description'));
        if (hasConfirmed) {
            setLogs([]);
            setIsSuccess(false);
            setIsStreaming(true);

            try {
                const formData = new FormData();
                formData.append('file', file);

                const response = await fetch(`${ENV.API_URL}/scripts/edit_product_info_from_product_id`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ message: response.statusText }));
                    const errorMessage = errorData?.message || errorData?.error || response.statusText;
                    setLogs((prev) => [...prev, `❌ Erreur: ${errorMessage}`]);
                    return;
                }

                if (!response.body) {
                    throw new Error('No response body');
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const parts = buffer.split('\n');
                    buffer = parts.pop() || '';

                    const newLogs = parts.filter((part) => part.trim() && !part.startsWith('{'));
                    if (newLogs.length > 0) {
                        setLogs((prev) => [...prev, ...newLogs]);
                    }
                }

                setIsSuccess(true);
            } catch (error) {
                console.error('Error:', error);
                // Only show fetch error if we don't already have error logs
                setLogs((prev) => {
                    const hasErrorLogs = prev.some((log) => log.includes('❌'));
                    if (hasErrorLogs) return prev;
                    return [...prev, `❌ Erreur: ${error}`];
                });
            } finally {
                setIsStreaming(false);
            }
        }
    };

    return (
        <Form<EditProductInfoFromProductIdInputs>>
            {({ handleSubmit }) => {
                return (
                    <>
                        <Text type="large" text="brand.adminTools.editProductInfoFromProductId.title" />

                        <TextContainer>
                            <DescriptionList>
                                <FormattedMessage
                                    id={`brand.adminTools.editProductInfoFromProductId.description1`}
                                    values={{
                                        metabaseLink: (
                                            <a
                                                href={`https://metabase-prod.innovorder.io/question/5434`}
                                                target="_blank"
                                            >
                                                <FormattedMessage id="file.metabaseLink" />
                                            </a>
                                        ),
                                    }}
                                />
                            </DescriptionList>
                        </TextContainer>
                        <FormControls>
                            <FormControl>
                                <DropzoneContainer>
                                    <React.Suspense>
                                        <FileInput
                                            mode="compact"
                                            accept={ALLOWED_FILE_TYPES}
                                            name="file"
                                            transform={{
                                                output: (file) => file[0],
                                                input: (file: File) => (file ? [file] : []),
                                            }}
                                        />
                                    </React.Suspense>
                                </DropzoneContainer>
                            </FormControl>

                            {logs.length > 0 && (
                                <LogsContainer>
                                    {logs.map((log, index) => (
                                        <LogLine
                                            key={index}
                                            isStreaming={isStreaming && index === logs.length - 1}
                                            isSuccess={false}
                                        >
                                            {log}
                                        </LogLine>
                                    ))}
                                </LogsContainer>
                            )}

                            <SvyButton onClick={handleSubmit(handleSubmitForm)} disabled={isStreaming}>
                                <FormattedMessage id="button.validate" />
                            </SvyButton>
                            {isSuccess && (
                                <SuccessMessage text="brand.adminTools.editProductInfoFromProductId.form.success" />
                            )}
                        </FormControls>
                    </>
                );
            }}
        </Form>
    );
};

export default EditProductInfoFromProductId;
